import React, { useRef, memo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

/**
 * VirtualList - Virtualized list component for performance
 * @param {Array} items - Array of items to render
 * @param {Function} renderItem - Function to render each item
 * @param {number} estimateSize - Estimated size of each item in pixels
 * @param {string} className - Additional CSS classes
 */
export const VirtualList = memo(function VirtualList({ 
  items, 
  renderItem, 
  estimateSize = 200,
  className = '',
  gap = 16 
}) {
  const parentRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 5,
  });

  return (
    <div
      ref={parentRef}
      className={`overflow-auto scrollbar-thin ${className}`}
      style={{ height: '100%' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start + virtualRow.index * gap}px)`,
              }}
            >
              {renderItem(item, virtualRow.index)}
            </div>
          );
        })}
      </div>
    </div>
  );
});

VirtualList.displayName = 'VirtualList';

/**
 * VirtualGrid - Virtualized grid component for performance
 * @param {Array} items - Array of items to render
 * @param {Function} renderItem - Function to render each item
 * @param {number} columnCount - Number of columns
 * @param {number} itemHeight - Height of each item in pixels
 * @param {number} gap - Gap between items in pixels
 * @param {string} className - Additional CSS classes
 */
export const VirtualGrid = memo(function VirtualGrid({ 
  items, 
  renderItem, 
  columnCount = 3,
  itemHeight = 200,
  gap = 16,
  className = '' 
}) {
  const parentRef = useRef(null);
  
  // Calculate rows
  const rowCount = Math.ceil(items.length / columnCount);
  
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight + gap,
    overscan: 2,
  });

  return (
    <div
      ref={parentRef}
      className={`overflow-auto scrollbar-thin ${className}`}
      style={{ height: '100%' }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const startIdx = virtualRow.index * columnCount;
          const endIdx = Math.min(startIdx + columnCount, items.length);
          const rowItems = items.slice(startIdx, endIdx);
          
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div 
                className="grid" 
                style={{ 
                  gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                  gap: `${gap}px`
                }}
              >
                {rowItems.map((item, colIndex) => (
                  <div key={startIdx + colIndex}>
                    {renderItem(item, startIdx + colIndex)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

VirtualGrid.displayName = 'VirtualGrid';